// ─── WhimerV3View — the v3 two-zone view (code-audit §5.1) ─────────────────────────
//
// ONE core, two skins (clean3 / term3). This is the pure expression of the code-audit
// recommendation §5.1: for a JSONL-backed agent, the COMMITTED transcript is sourced
// SOLELY from the agent's own JSONL (structured turns), and the LIVE zone is SOLELY the
// recognized terminal frame (native_frame). It deliberately does NOT consume `native_commit`
// at all — that channel carries the oracle/watermark's pixel-derived committed lines, which
// is the source of the torn-table / missing-line / out-of-order bugs (RCA 2026-06-10). By
// never deriving "committed" from rendered terminal cells, that whole class of bug cannot
// occur here: tables/Thai/code render whole because they come from markdown source, not cells.
//
// How it differs from Clean² (WhimerCleanView): Clean² still subscribes to native_commit to
// show scroll-off prose live (`pending`) and flips it on `promote` — that ties it to the
// oracle. V3 drops that entirely. The trade-off (documented + accepted in §5.1 / the
// first-principles doc): if a long response scrolls off the live frame before it finalizes,
// those lines briefly leave the live zone — but the COMPLETE turn lands in committed the
// moment its JSONL turn is written. No data is ever lost; nothing is ever torn.
//
//   skin='clean'  → WhimerClean³: chat bubbles (TurnItem default) — friendly, general-user.
//   skin='term'   → WhimerTerm³:  terminal transcript skin (monospace, ❯ prompts) via the
//                                 `.whimerv3--term` CSS scope. Same data, same reliability.
//
// native-pty only (the live zone is fed by the v2 native pipeline). The committed pipeline
// (load/merge/paginate/cache/optimistic/queued) is ported faithfully from WhimerCleanView so
// behaviour parity holds for everything except the removed oracle dependency.

import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import type { Chat, ConversationTurn, NativeIR, PromptOption } from '../api/client'
import { api } from '../api/client'
import type { UseWebSocketReturn } from '../hooks/useWebSocket'
import { TurnItem } from './turns/TurnItem'
import { WhimerComposer } from './whimer/WhimerComposer'
import { renderNativeWidget, groupNativeWidgets, widgetDiffKinds } from './whimer/NativeWidgets'
import { usePromptCardHide, stablePromptKey } from './whimer/promptHide'
import { AboveInputZone, type ZoneChip } from './whimer/AboveInputZone'
import { getSlotContributions } from '../slots'
import { renderSlotNode } from '../slots/WidgetView'
import type { SlotContext } from '../slots/types'
import { useStickToBottom } from '../hooks/useStickToBottom'
import { useLiveCollapse } from '../hooks/useLiveCollapse'
import { sendKeysSeq } from '../lib/sendKeys'
import { reconcileLocalTurns, applyStructuredTurn, isPending, gist } from '../lib/turnMerge'
import { Icon } from './Icon'

interface Props {
  chatId: string
  chat: Chat
  wsHook: UseWebSocketReturn
  /** 'clean' = chat bubbles (Clean³) · 'term' = terminal transcript (Term³). Same data core. */
  skin: 'clean' | 'term'
  /** Hide the live terminal zone + source committed SOLELY from the JSONL re-read (reconcile).
   *  Driven by the plugin's `capabilities.liveView === false` (e.g. grok) — NOT a per-agent hardcode.
   *  AppShell resolves it from capsFor(chat) and passes it down. */
  liveDisabled?: boolean
  refreshNonce?: number
  onActivity?: () => void
  onToggleVoice?: () => void
  voiceActive?: boolean
}

const PAGE_SIZE = 10

// Per-chat committed cache (module-level → survives AppShell's key={chatId} remount + the
// refreshNonce refetch storm). Seeds the committed zone INSTANTLY on return so a slow/failed
// refetch never blanks it. Shared across both skins (the committed data is identical). Mirrors
// WhimerCleanView's cache, kept separate so the two views never fight over one entry.
interface CommittedCache { turns: ConversationTurn[]; oldest: number; hasMore: boolean }
const v3CommittedCache = new Map<string, CommittedCache>()
const CACHE_MAX = 40
function cacheCommitted(id: string, v: CommittedCache) {
  v3CommittedCache.delete(id)            // re-insert → most-recently-used at the end
  v3CommittedCache.set(id, v)
  while (v3CommittedCache.size > CACHE_MAX) {
    const oldest = v3CommittedCache.keys().next().value
    if (oldest === undefined) break
    v3CommittedCache.delete(oldest)
  }
}

export function WhimerV3View({ chatId, chat, wsHook, skin, liveDisabled = false, refreshNonce, onActivity, onToggleVoice, voiceActive }: Props) {
  const [turns, setTurns] = useState<ConversationTurn[]>(() => v3CommittedCache.get(chatId)?.turns ?? [])
  const [historyLoaded, setHistoryLoaded] = useState(() => (v3CommittedCache.get(chatId)?.turns.length ?? 0) > 0)
  const [isWaiting, setIsWaiting] = useState(false)
  const [ir, setIr] = useState<NativeIR>({ widgets: [] })      // v2 native-pipeline live frame (the ONLY live source)
  // liveDisabled (plugin capability liveView===false, e.g. grok): hide the live terminal zone +
  // source committed SOLELY from the JSONL re-read (reconcile). No live-push merge → no double.

  // 3-step collapse of the live zone (full / min / medium) — persisted per chat.
  const { state: liveCollapse, cycle: cycleLive } = useLiveCollapse(chatId)
  const liveBoxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = liveBoxRef.current
    if (el && liveCollapse !== 3) el.scrollTop = el.scrollHeight
  }, [liveCollapse, ir])

  // Auto-scroll: follow live output while at bottom, yield on scroll-up, re-engage at bottom.
  const { scrollRef, contentRef, showScrollDown, goToBottom, handleScroll: stickScroll, stickRef, runProgrammatic } = useStickToBottom(chatId)
  const localSeq = useRef(0)

  // Auto-resync (user 2026-06-11): after the AI appends turns to its JSONL, debounce-refetch the
  // latest window + reconcile → correct ORDER (e.g. fast double-sends that arrive user,user,ai,ai).
  // reconcile re-reads the whole window in ONE read so offsets are self-consistent — this is the
  // SAFE way to fix order, unlike sorting live-appended turns against stale offsets (BUG-024).
  const resyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // BUG-1 (2026-06-18): pane.state of THIS chat, tracked to detect the running/waiting→idle edge.
  const prevPaneState = useRef<string | undefined>(undefined)
  const scheduleResync = useCallback(() => {
    if (resyncTimer.current) clearTimeout(resyncTimer.current)
    resyncTimer.current = setTimeout(async () => {
      try {
        const h = await api.chats.structuredHistory(chatId, PAGE_SIZE)
        setTurns(prev => reconcileLocalTurns(prev, h.turns, Date.now()))
      } catch { /* transient — the next structured_turn reschedules */ }
    }, 600)
  }, [chatId])
  useEffect(() => () => { if (resyncTimer.current) clearTimeout(resyncTimer.current) }, [])

  // Lazy-load (pagination) of older committed turns — load the latest PAGE_SIZE first; scrolling
  // near the top pulls the previous page. Uses the structured-history ?before cursor.
  const [hasMoreOlder, setHasMoreOlder] = useState(() => v3CommittedCache.get(chatId)?.hasMore ?? false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const oldestLoadedRef = useRef(v3CommittedCache.get(chatId)?.oldest ?? 0)
  const loadingOlderRef = useRef(false)
  const pendingPrependRef = useRef<{ prevHeight: number; prevTop: number } | null>(null)
  const { rec: hideRec, set: setHideRec } = usePromptCardHide(chatId)

  const senderLabel =
    chat.agent_display_name ??
    (chat.agent_plugin ? chat.agent_plugin[0].toUpperCase() + chat.agent_plugin.slice(1) : 'Assistant')

  // Reset only the volatile LIVE-zone state on chat switch. Committed state is seeded from the
  // cache (NOT reset) so a return visit shows instantly instead of blanking.
  useEffect(() => {
    setIsWaiting(false); setIr({ widgets: [] })
    setLoadingOlder(false); loadingOlderRef.current = false; pendingPrependRef.current = null
    prevPaneState.current = undefined
    if (resyncTimer.current) { clearTimeout(resyncTimer.current); resyncTimer.current = null }
  }, [chatId])

  // Persist committed state so a return visit (after the key remount) seeds instantly.
  useEffect(() => {
    cacheCommitted(chatId, { turns, oldest: oldestLoadedRef.current, hasMore: hasMoreOlder })
  }, [chatId, turns, hasMoreOlder])

  // Committed (JSONL) load — resilient: time-box each attempt + retry with backoff so a hung
  // socket / WS blip can't leave the committed zone blank with no recovery. On a refreshNonce
  // refetch existing turns stay put (setTurns runs only on success). Ported from WhimerCleanView.
  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const MAX_RETRIES = 8
    const TIMEOUT_MS  = 8000
    const hadData = (v3CommittedCache.get(chatId)?.turns.length ?? 0) > 0
    const withTimeout = <T,>(p: Promise<T>): Promise<T> =>
      Promise.race([
        p,
        new Promise<T>((_, rej) => setTimeout(() => rej(new Error('history-timeout')), TIMEOUT_MS)),
      ])

    const attempt = (n: number) => {
      withTimeout(api.chats.structuredHistory(chatId, PAGE_SIZE))
        .then(h => {
          if (cancelled) return
          setTurns(prev => reconcileLocalTurns(prev, h.turns, Date.now()))
          if (!hadData) {
            oldestLoadedRef.current = h.oldestLoaded ?? 0
            setHasMoreOlder(h.hasMore ?? false)
          }
          setHistoryLoaded(true)
          if (!hadData && h.turns.length === 0 && n === 0) {
            timer = setTimeout(() => attempt(1), 1500)
          }
        })
        .catch(() => {
          if (cancelled) return
          if (n < MAX_RETRIES) {
            timer = setTimeout(() => attempt(n + 1), Math.min(800 * 2 ** n, 6000))
          } else {
            setHistoryLoaded(true)
          }
        })
    }
    attempt(0)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [chatId, refreshNonce])

  // Keep the viewport anchored on the same turn after prepending an older page (no jump).
  useLayoutEffect(() => {
    const pending = pendingPrependRef.current
    if (!pending) return
    pendingPrependRef.current = null
    const el = scrollRef.current
    if (!el) return
    stickRef.current = false
    runProgrammatic(() => { el.scrollTop = el.scrollHeight - pending.prevHeight + pending.prevTop })
  }, [turns]) // eslint-disable-line react-hooks/exhaustive-deps

  // Live committed turns from JSONL (structured_turn) — replace-pending-then-dedup (applyStructuredTurn).
  useEffect(() => {
    wsHook.onStructuredTurn((turn, cId) => {
      if (cId !== chatId) return
      // grok (liveDisabled): DON'T merge the live turn — grok's byte-offset ids shift mid-stream so a
      // live-push turn can't reliably dedup against the JSONL re-read → double-render. Use the push only
      // as a signal to refresh committed from the authoritative readSession (reconcile). No append, no dup.
      if (!liveDisabled) setTurns(prev => applyStructuredTurn(prev, turn))
      if (turn.role === 'assistant' || turn.role === 'thinking') setIsWaiting(false)
      scheduleResync()   // debounce-refetch → correct order via reconcile (BUG-024: not a client byte-sort)
      onActivity?.()
    })
  }, [chatId, wsHook, onActivity, scheduleResync, liveDisabled])

  // BUG-1 reconcile-on-idle (RCA 2026-06-18 §10): the live `structured_turn` WS push is
  // fire-and-forget — if it's lost (socket mid-reconnect exactly when the turn finalizes), the
  // finished turn never lands in committed until a manual reload. Independent safety net: when this
  // chat's pane.state transitions running/waiting → idle (turn done), re-read structured-history and
  // reconcile. Re-read is AUTHORITATIVE (probe: 5/5 turns always present), so a dropped push self-
  // heals the instant the agent goes idle. Reuses scheduleResync's debounced re-fetch + reconcile
  // (dedup by srcKey/id) so a redundant reconcile can never double-render. Fires only on the →idle
  // EDGE (not every render). Scope = ALL native agents (grok hits it more often due to write timing).
  useEffect(() => {
    const st = wsHook.paneStates[chatId]?.state
    const prev = prevPaneState.current
    prevPaneState.current = st
    if (st === 'idle' && (prev === 'running' || prev === 'waiting')) scheduleResync()
  }, [chatId, wsHook.paneStates, scheduleResync])

  // Live frame — the ONLY live source (no native_commit). native-pty streams native IR.
  useEffect(() => {
    wsHook.onNativeFrame((next, cId) => { if (cId === chatId) setIr(next) })
  }, [chatId, wsHook])

  useEffect(() => {
    wsHook.onAgentActivity((cId, active) => { if (cId === chatId) setIsWaiting(active) })
  }, [chatId, wsHook])

  // Active checker (3s): sweep optimistic turns whose content has since landed in JSONL (a
  // committed twin is loaded) so they don't hang forever if a WS structured_turn replace was
  // missed. Guards duplicate-content sends: fast-reap (5s) only when the twin is NEWER than the
  // optimistic send time; older/unknown twin → wait 30s (let WS/refetch settle first). Ported.
  useEffect(() => {
    const iv = setInterval(() => {
      setTurns(prev => {
        const confirmedUser = prev.filter(t => !isPending(t.id) && t.role === 'user')
        if (confirmedUser.length === 0) return prev
        const now = Date.now()
        const confirmedStuck = (t: ConversationTurn) => {
          if (!isPending(t.id) || t.role !== 'user') return false
          const twins = confirmedUser.filter(x => x.content.trim() === t.content.trim())
          if (!twins.length) return false
          if (t.id.startsWith('submit-')) return true
          const sentAt = Number(t.id.split('-')[1]) || 0
          const hasNewerTwin = twins.some(x => {
            const xt = x.timestamp ? Date.parse(String(x.timestamp)) : NaN
            return !Number.isNaN(xt) && xt >= sentAt - 15000
          })
          return now - sentAt > (hasNewerTwin ? 5000 : 30000)
        }
        return prev.some(confirmedStuck) ? prev.filter(t => !confirmedStuck(t)) : prev
      })
    }, 3000)
    return () => clearInterval(iv)
  }, [chatId])

  // Backend says input was dropped (no live PTY) → fail every optimistic 'sending' immediately.
  useEffect(() => {
    wsHook.onInputDropped((cId) => {
      if (cId !== chatId) return
      setTurns(prev => prev.map(t => t.status === 'sending' ? { ...t, status: 'failed' } : t))
      setIsWaiting(false)
    })
  }, [chatId, wsHook])

  useEffect(() => {
    if (wsHook.authState !== 'authenticated') return
    wsHook.attachChat(chatId, 0, 0)
    return () => { wsHook.detachChat() }
  }, [chatId, wsHook.authState]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = () => {
    stickScroll()
    const el = scrollRef.current
    if (el && el.scrollTop < 80 && hasMoreOlder && !loadingOlderRef.current) loadOlder()
  }

  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current || !hasMoreOlder) return
    const cursor = oldestLoadedRef.current
    if (cursor <= 0) return
    loadingOlderRef.current = true
    setLoadingOlder(true)
    const el = scrollRef.current
    pendingPrependRef.current = el ? { prevHeight: el.scrollHeight, prevTop: el.scrollTop } : null
    try {
      const h = await api.chats.structuredHistory(chatId, PAGE_SIZE, cursor)
      setTurns(prev => {
        const existing = new Set(prev.map(t => t.id))
        const older = h.turns.filter(t => !existing.has(t.id))
        return older.length ? [...older, ...prev] : prev
      })
      oldestLoadedRef.current = h.oldestLoaded ?? 0
      setHasMoreOlder(h.hasMore ?? false)
    } catch (err) {
      console.error('[WhimerV3View] load older failed:', err)
      pendingPrependRef.current = null
    } finally {
      loadingOlderRef.current = false
      setLoadingOlder(false)
    }
  }, [chatId, hasMoreOlder])

  const scrollToBottom = goToBottom

  const scheduleFail = useCallback((id: string) => {
    setTimeout(() => setTurns(prev => prev.map(t =>
      t.id === id && t.status === 'sending' ? { ...t, status: 'failed' } : t)), 20000)
  }, [])

  const handleSend = useCallback((content: string) => {
    const sending = wsHook.authState === 'authenticated'
    const lastAssistantIdx = turns.map(t => t.role).lastIndexOf('assistant')
    const hasUnansweredUser = turns.slice(lastAssistantIdx + 1).some(t => t.role === 'user')
    const queued = sending && (isWaiting || hasUnansweredUser)
    const turn: ConversationTurn = {
      id: `local-${Date.now()}-${++localSeq.current}`,
      role: 'user', content,
      status: sending ? (queued ? 'queued' : 'sending') : 'failed',
    }
    setTurns(prev => [...prev, turn])
    stickRef.current = true
    if (!sending) return
    setIsWaiting(true)
    wsHook.submitMessage(chatId, content)
    if (!queued) scheduleFail(turn.id)
    onActivity?.()
  }, [wsHook, chatId, onActivity, isWaiting, turns, scheduleFail]) // eslint-disable-line react-hooks/exhaustive-deps

  // C2 — composer-above slot: external/plugin contributions flow into AboveInputZone via the slot
  // registry instead of being hardcoded here. Empty registry → no extra chips (today's behaviour).
  const slotCtx: SlotContext = {
    chatId,
    // LIVE state from isWaiting (reactive WS signal), NOT chat.paneState (a stale prop snapshot —
    // AppShell only updates chat.state, never chat.paneState, so the old read was always 'idle'
    // and `when: idle` widgets never hid while running). running↔idle is the distinction slot
    // `when` conditions need today; waiting/error collapse to 'idle' until a 4-state live feed exists.
    paneState: isWaiting ? 'running' : 'idle',
    agent: chat?.agent_plugin ?? '',
    sendInput: (d) => wsHook.sendInput(d),
    submit: handleSend,
    close: () => {},
  }
  const aboveChips: ZoneChip[] = getSlotContributions('composer-above', slotCtx)
    .map((c) => ({ id: c.id, node: renderSlotNode(c, slotCtx) }))
    .filter((z): z is ZoneChip => z.node != null)

  const retryTurn = useCallback((turn: ConversationTurn) => {
    if (wsHook.authState !== 'authenticated') return
    setTurns(prev => prev.map(t => t.id === turn.id ? { ...t, status: 'sending' } : t))
    wsHook.submitMessage(chatId, turn.content)
    setIsWaiting(true)
    scheduleFail(turn.id)
  }, [wsHook, chatId, scheduleFail])
  const dismissTurn = useCallback((id: string) => {
    setTurns(prev => prev.filter(t => t.id !== id))
  }, [])

  // ESC recall (terminal parity): pull the LAST user message (the one just sent / in-flight /
  // interrupted) back into the composer to edit + resend. The message to recall = the most recent
  // user turn AFTER the last assistant reply — recalled whether it's still a local optimistic turn
  // OR already committed to JSONL (claude commits a queued message immediately, so by the time Esc
  // lands it's usually committed — recalling only `pending` ones missed it = "ไม่เด้งกลับ"). A
  // pending turn is dropped from the view; a committed one stays (claude owns the JSONL, can't
  // un-send). The double-send is prevented backend-side: submitMessage now clears the input first,
  // so the edited resend can't concatenate with whatever claude bounced back into its own input.
  const recallLast = useCallback((): string | null => {
    let recalled: string | null = null
    setTurns(prev => {
      let lastAssistant = -1
      for (let i = prev.length - 1; i >= 0; i--) { if (prev[i].role === 'assistant') { lastAssistant = i; break } }
      for (let i = prev.length - 1; i > lastAssistant; i--) {
        if (prev[i].role === 'user') {
          recalled = prev[i].content
          return isPending(prev[i].id) ? prev.filter((_, j) => j !== i) : prev
        }
      }
      return prev
    })
    return recalled
  }, [])

  const answerPrompt = useCallback((opt: PromptOption) => {
    if (wsHook.authState !== 'authenticated') return
    sendKeysSeq((k) => wsHook.sendInput(k), opt.send)
  }, [wsHook])

  const runMenuItem = useCallback((send: string) => {
    if (wsHook.authState === 'authenticated') sendKeysSeq((k) => wsHook.sendInput(k), '\x15' + send + '\r')
  }, [wsHook])

  // live-presence "รอคิว": claude writes the user turn to JSONL the instant it queues → the
  // bubble would become committed while still queued. The only true signal is the screen: a
  // queued message renders as a `❯ …` line BELOW the spinner; the one being processed echoes
  // ABOVE it. Match the ❯ lines after the spinner = genuinely queued. (Same as WhimerCleanView.)
  const queuedLineGists = useMemo(() => {
    let spinnerOrder = -1
    for (const w of ir.widgets) {
      if ((w.kind as string) === 'spinner') { spinnerOrder = w.order ?? -1; break }
    }
    if (spinnerOrder < 0) return []
    const out: string[] = []
    for (const w of ir.widgets) {
      if (w.kind !== 'raw' || (w.order ?? 0) <= spinnerOrder) continue
      const d = w.data as { line?: { segments: { text: string }[] } } | undefined
      const text = (d?.line ? d.line.segments.map(s => s.text).join('') : '').trim()
      if (!/^[❯>]/.test(text)) continue
      const g = gist(text)
      if (g.length >= 2) out.push(g)
    }
    return out
  }, [ir])
  const lastAnsweredIdx = useMemo(
    () => turns.map(t => t.role).lastIndexOf('assistant'),
    [turns],
  )
  const isQueuedLive = useCallback((turn: ConversationTurn, idx: number): boolean => {
    if (turn.role !== 'user' || idx <= lastAnsweredIdx || queuedLineGists.length === 0) return false
    const g = gist(turn.content)
    if (g.length < 2) return false
    return queuedLineGists.some(lg =>
      lg === g || (lg.length >= 6 && g.startsWith(lg)) || (g.length >= 6 && lg.startsWith(g)))
  }, [queuedLineGists, lastAnsweredIdx])

  // Live widgets: drop any tagged `committed` (already shown as a JSONL turn above) so a finished
  // response isn't duplicated live. IMPORTANT (accurate framing): this `committed` flag IS computed
  // by the committed-oracle watermark (ptyManager.tagCommittedFrame → getCommittedOracle). So the
  // oracle is NOT fully gone — but here it only drives LIVE-ZONE dedup, never the committed transcript
  // (which is JSONL). §5.1's real win = the oracle is DEMOTED from load-bearing (Term² promotes
  // rendered cells INTO the committed log → a wrong watermark = permanently torn/missing transcript)
  // to cosmetic (a wrong watermark here = a finished line shows in both zones, or a live line is hidden,
  // transiently — self-heals when the turn finalizes in JSONL). Making this dedup oracle-free too is
  // Task 5 (backend classifier: tag committed by msgId/JSONL, not the watermark).
  const grouped = groupNativeWidgets(ir)
  const aboveFlow = grouped.aboveFlow.filter(w => !w.committed)
  const aboveFlowDiffKinds = widgetDiffKinds(aboveFlow)
  const { abovePinned, below, overlay } = grouped
  const promptWg = abovePinned.find((w) => (w.kind as string) === 'prompt')
  const promptKey = promptWg ? stablePromptKey(promptWg.data as { question: string; options: { label: string }[] }) : null
  const promptHidden = !!(hideRec && promptKey && hideRec.key === promptKey && hideRec.mode === 'hidden')
  const promptDismissed = !!(hideRec && promptKey && hideRec.key === promptKey && hideRec.mode === 'dismissed')
  const pinned = abovePinned.filter((w) => !(w === promptWg && (promptHidden || promptDismissed)))
  const handlers = {
    onAnswer: answerPrompt, onRunMenu: runMenuItem,
    onDismiss: promptKey ? () => setHideRec({ key: promptKey, mode: 'dismissed' as const }) : undefined,
    onHide: promptKey ? () => setHideRec({ key: promptKey, mode: 'hidden' as const }) : undefined,
  }

  const rootClass = `whimerterm whimerv3 whimerv3--${skin}`

  return (
    <div className={rootClass}>
      <div className="chat-turns" ref={scrollRef} onScroll={handleScroll}>
        <div ref={contentRef}>
        {!historyLoaded && <div className="chat-turns-loading"><span className="spin-icon">◌</span> Loading...</div>}
        {historyLoaded && loadingOlder && <div className="chat-turns-loading-older"><span className="spin-icon">◌</span> กำลังโหลดข้อความเก่า...</div>}
        {historyLoaded && turns.length === 0 && <div className="chat-turns-empty">Start the conversation</div>}
        {turns.map((turn, idx) => (
          <TurnItem key={turn.id} turn={turn} senderLabel={senderLabel} onInput={(text) => wsHook.sendInput(text)} chatId={chatId} onRetry={retryTurn} onDismiss={dismissTurn} queued={isQueuedLive(turn, idx)} richTools agent={chat.agent_plugin ?? undefined} />
        ))}
        {/* boundary: committed (settled JSONL turns) above ↑ / live below ↓ */}
        {historyLoaded && (
          <div className={`wterm-divider${isWaiting ? ' is-running' : ''}`}>
            <span className="wterm-divider-dot">●</span>
            <span className="wterm-divider-label">
              {liveDisabled
                ? (isWaiting ? 'กำลังพิมพ์…' : 'พร้อม')
                : (isWaiting ? 'กำลังรัน / สตรีมอยู่' : 'พร้อม (ค้างแล้ว)')}
            </span>
          </div>
        )}
        {/* Live zone: native informational widgets only (spinner / raw stream / status). No
            pending lines — the frame already shows the in-progress tail (§5.1). Hidden for grok
            (liveDisabled): its terminal frame is unreadable colour-bands — pane-state above is the
            only live signal; committed fills from JSONL when the turn finalizes. */}
        {aboveFlow.length > 0 && !liveDisabled && (
          <div className={`wnative-livebox livebox-${liveCollapse}`} ref={liveBoxRef}>
            <div className="wnative-live">
              {aboveFlow.map((wg, i) => renderNativeWidget(wg, `a-${i}`, handlers, aboveFlowDiffKinds[i]))}
            </div>
          </div>
        )}
        </div>{/* /contentRef */}
      </div>

      {overlay.length > 0 && !liveDisabled && (
        <div className="wnative-overlay">
          {overlay.map((wg, i) => renderNativeWidget(wg, `o-${i}`, handlers))}
        </div>
      )}

      <div className="whimer-live">
        {showScrollDown && (
          <button className="chat-scroll-bottom-btn" onClick={scrollToBottom} title="ลงล่างสุด" aria-label="Scroll to latest" type="button">
            <Icon name="chevron-down" size={20} />
          </button>
        )}
        <AboveInputZone
          chips={[
            // Live-collapse toggle is meaningless when the live zone is hidden (grok) — drop it.
            !liveDisabled && {
              id: 'live-collapse',
              node: (
                <button className="livebox-toggle" type="button" onClick={cycleLive} title="ย่อ/ขยาย live zone (เต็ม → ย่อ → กลาง)">
                  <Icon name={liveCollapse === 3 ? 'chevron-down' : 'chevron-up'} size={13} />
                  {liveCollapse === 3 ? 'Live: เต็ม' : liveCollapse === 2 ? 'Live: กลาง' : 'Live: ย่อ'}
                </button>
              ),
            },
            promptHidden && {
              id: 'restore',
              node: <button className="iprompt-restore" type="button" onClick={() => setHideRec(null)} title="แสดงคำถามที่ซ่อนไว้">ดูคำถาม</button>,
            },
            ...aboveChips,
          ].filter(Boolean) as ZoneChip[]}
          cards={pinned.length > 0 && !liveDisabled ? (
            <div className="wnative-live wnative-pinned">
              {pinned.map((wg, i) => renderNativeWidget(wg, `p-${i}`, handlers))}
            </div>
          ) : null}
        />
        <WhimerComposer chatId={chatId} senderLabel={senderLabel} terminal={skin === 'term'} disabled={!historyLoaded} onSubmit={handleSend} sendInput={(d) => wsHook.sendInput(d)} onToggleVoice={onToggleVoice} voiceActive={voiceActive} onRecall={recallLast} agent={slotCtx.agent} paneState={slotCtx.paneState} />
        {below.length > 0 && !liveDisabled && (
          <div className="wnative-live wnative-below">
            {below.map((wg, i) => renderNativeWidget(wg, `b-${i}`, handlers))}
          </div>
        )}
      </div>
    </div>
  )
}
